import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useForm, Controller } from 'react-hook-form';
import { CustomInput } from '../components/CustomInput';
import { DatePickerInput } from '../components/DatePickerInput';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import ApiService from '../services/ApiService';
import { formatCurrency, numericValidation } from '../utils/helpers';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

interface SalaryPaymentFormData {
    month: string;
    paidAmount: string;
    date: string;
    notes: string;
}

const AddSalaryPaymentScreen = ({ navigation, route }: any) => {
    const { employeeId, employeeName, fixedSalary } = route.params;

    const today = new Date();
    const currentMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        isSuccess?: boolean;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<SalaryPaymentFormData>({
        defaultValues: {
            month: currentMonth,
            paidAmount: '',
            date: today.toISOString(),
            notes: '',
        },
        mode: 'onChange',
    });

    const month = watch('month');
    const paidAmountValue = watch('paidAmount');

    // Parse month for display
    const getMonthDisplay = (monthStr: string) => {
        const [year, monthNum] = monthStr.split('-');
        return `${MONTH_NAMES[parseInt(monthNum, 10) - 1]} ${year}`;
    };

    const handleModalClose = () => {
        const isSuccess = modal.isSuccess;
        setModal(prev => ({ ...prev, visible: false }));
        if (isSuccess) {
            navigation.goBack();
        }
    };

    const onSubmit = useCallback(async (data: SalaryPaymentFormData) => {
        setLoading(true);
        try {
            await ApiService.addSalaryTransaction(employeeId, {
                month: data.month,
                paidAmount: Number(data.paidAmount),
                date: data.date,
                notes: data.notes.trim() || undefined,
            });

            const paidAmount = Number(data.paidAmount);
            let message = 'Salary payment recorded successfully.';
            if (paidAmount > fixedSalary) {
                const advance = paidAmount - fixedSalary;
                message = `Payment recorded. Extra ₹${advance.toLocaleString()} will be shown as Advance.`;
            } else if (paidAmount < fixedSalary) {
                const pending = fixedSalary - paidAmount;
                message = `Payment recorded. Remaining ₹${pending.toLocaleString()} will be shown as Pending.`;
            }

            setModal({
                visible: true,
                type: 'success',
                title: 'Success',
                message,
                isSuccess: true,
            });
        } catch (error) {
            console.error('Error saving salary payment:', error);
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: 'Failed to save payment. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    }, [employeeId, fixedSalary]);

    const handlePreviousMonth = () => {
        const [year, monthNum] = month.split('-');
        let newMonth = parseInt(monthNum, 10) - 1;
        let newYear = parseInt(year, 10);
        if (newMonth < 1) {
            newMonth = 12;
            newYear -= 1;
        }
        setValue('month', `${newYear}-${String(newMonth).padStart(2, '0')}`);
    };

    const handleNextMonth = () => {
        const [year, monthNum] = month.split('-');
        let newMonth = parseInt(monthNum, 10) + 1;
        let newYear = parseInt(year, 10);
        if (newMonth > 12) {
            newMonth = 1;
            newYear += 1;
        }
        setValue('month', `${newYear}-${String(newMonth).padStart(2, '0')}`);
    };

    const paidAmount = Number(paidAmountValue) || 0;
    const difference = paidAmount - fixedSalary;

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <ScreenHeader
                title="Add Salary Payment"
                showBackButton={true}
                navigation={navigation}
            />

            <KeyboardAwareScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.contentContainer}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >
                <View style={styles.form}>
                    {/* Employee Info Card */}
                    <View style={styles.infoCard}>
                        <Icon name="account" size={32} color="#2196F3" />
                        <View style={styles.infoContent}>
                            <Text style={styles.employeeName}>{employeeName}</Text>
                            <Text style={styles.fixedSalary}>
                                Fixed Salary: {formatCurrency(fixedSalary)}/month
                            </Text>
                        </View>
                    </View>

                    {/* Month Selector */}
                    <View style={styles.monthSelector}>
                        <Text style={styles.label}>Select Month *</Text>
                        <View style={styles.monthRow}>
                            <TouchableOpacity
                                style={styles.monthArrow}
                                onPress={handlePreviousMonth}
                            >
                                <Icon name="chevron-left" size={28} color={theme.colors.primary} />
                            </TouchableOpacity>
                            <View style={styles.monthDisplay}>
                                <Text style={styles.monthText}>{getMonthDisplay(month)}</Text>
                            </View>
                            <TouchableOpacity
                                style={styles.monthArrow}
                                onPress={handleNextMonth}
                            >
                                <Icon name="chevron-right" size={28} color={theme.colors.primary} />
                            </TouchableOpacity>
                        </View>
                    </View>

                    <Controller
                        control={control}
                        name="date"
                        rules={{ required: 'Payment date is required' }}
                        render={({ field: { onChange, value } }) => (
                            <DatePickerInput
                                label="Payment Date *"
                                value={value}
                                onChangeDate={onChange}
                                error={errors.date?.message}
                                maximumDate={new Date()}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="paidAmount"
                        rules={{ required: 'Amount is required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Paid Amount (₹) *"
                                value={value}
                                onChangeText={(text) => onChange(numericValidation(text))}
                                onBlur={onBlur}
                                icon="currency-inr"
                                keyboardType="number-pad"
                                error={errors.paidAmount?.message}
                                placeholder={`e.g., ${fixedSalary}`}
                            />
                        )}
                    />

                    {/* Amount Difference Display */}
                    {paidAmount > 0 && (
                        <View style={[
                            styles.differenceCard,
                            { backgroundColor: difference > 0 ? '#E8F5E9' : difference < 0 ? '#FFF3E0' : '#E3F2FD' }
                        ]}>
                            <Icon
                                name={difference > 0 ? 'arrow-up-circle' : difference < 0 ? 'clock-outline' : 'check-circle'}
                                size={24}
                                color={difference > 0 ? '#4CAF50' : difference < 0 ? '#FF9800' : '#2196F3'}
                            />
                            <View style={styles.differenceContent}>
                                <Text style={styles.differenceLabel}>
                                    {difference > 0 ? 'This will be added as Advance'
                                        : difference < 0 ? 'This will be shown as Pending'
                                            : 'Salary will be fully paid'}
                                </Text>
                                {difference !== 0 && (
                                    <Text style={[
                                        styles.differenceValue,
                                        { color: difference > 0 ? '#4CAF50' : '#FF9800' }
                                    ]}>
                                        {formatCurrency(Math.abs(difference))}
                                    </Text>
                                )}
                            </View>
                        </View>
                    )}

                    <Controller
                        control={control}
                        name="notes"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Notes (Optional)"
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                icon="note-text"
                                placeholder="Any additional information..."
                                multiline
                                numberOfLines={3}
                            />
                        )}
                    />

                    {/* Quick Amount Buttons */}
                    <View style={styles.quickAmounts}>
                        <Text style={styles.quickLabel}>Quick Select</Text>
                        <View style={styles.quickRow}>
                            <TouchableOpacity
                                style={styles.quickBtn}
                                onPress={() => setValue('paidAmount', String(fixedSalary))}
                            >
                                <Text style={styles.quickBtnText}>Full Salary</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={styles.quickBtn}
                                onPress={() => setValue('paidAmount', String(Math.round(fixedSalary / 2)))}
                            >
                                <Text style={styles.quickBtnText}>Half</Text>
                            </TouchableOpacity>
                        </View>
                    </View>

                    {/* Action Buttons */}
                    <View style={styles.buttonRow}>
                        <TouchableOpacity
                            style={styles.cancelBtn}
                            onPress={() => navigation.goBack()}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.saveBtn}
                            onPress={handleSubmit(onSubmit)}
                            activeOpacity={0.7}
                            disabled={loading}
                        >
                            <Text style={styles.saveBtnText}>
                                {loading ? 'Saving...' : 'Record Payment'}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={handleModalClose}
            />

            <LoadingOverlay visible={loading} message="Saving..." />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollView: {
        flex: 1,
    },
    contentContainer: {
        flexGrow: 1,
    },
    form: {
        padding: theme.spacing.lg,
    },
    infoCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#E3F2FD',
        padding: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        marginBottom: theme.spacing.lg,
        gap: theme.spacing.md,
    },
    infoContent: {
        flex: 1,
    },
    employeeName: {
        ...theme.typography.h6,
        color: theme.colors.text,
        fontWeight: '700',
    },
    fixedSalary: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    label: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        fontWeight: '600',
        marginBottom: theme.spacing.sm,
    },
    monthSelector: {
        marginBottom: theme.spacing.md,
    },
    monthRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    monthArrow: {
        width: 44,
        height: 44,
        borderRadius: theme.borderRadius.md,
        backgroundColor: theme.colors.surface,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    monthDisplay: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: theme.spacing.md,
        marginHorizontal: theme.spacing.sm,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    monthText: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
    },
    differenceCard: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: theme.spacing.md,
        borderRadius: theme.borderRadius.md,
        marginBottom: theme.spacing.md,
        gap: theme.spacing.md,
    },
    differenceContent: {
        flex: 1,
    },
    differenceLabel: {
        ...theme.typography.body2,
        color: theme.colors.text,
    },
    differenceValue: {
        ...theme.typography.h6,
        fontWeight: '700',
        marginTop: 2,
    },
    quickAmounts: {
        marginBottom: theme.spacing.md,
    },
    quickLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        fontWeight: '600',
        marginBottom: theme.spacing.sm,
    },
    quickRow: {
        flexDirection: 'row',
        gap: theme.spacing.sm,
    },
    quickBtn: {
        flex: 1,
        paddingVertical: theme.spacing.sm,
        backgroundColor: theme.colors.primary + '15',
        borderRadius: theme.borderRadius.md,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.primary + '30',
    },
    quickBtnText: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '600',
    },
    buttonRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.lg,
    },
    cancelBtn: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    cancelBtnText: {
        ...theme.typography.button,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    saveBtn: {
        flex: 2,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.primary,
        ...theme.shadows.sm,
    },
    saveBtnText: {
        ...theme.typography.button,
        color: '#FFFFFF',
        fontWeight: '700',
    },
});

export default AddSalaryPaymentScreen;
